import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import getCountryIso3 from 'country-iso-2-to-3';
import { Field, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { pxToRem } from '../../assets/styles/helper';
import SelectField from '../InputFields/SelectField';
import TextField from '../InputFields/TextField';
import {
  USA_COUNTRY_ISO3CODE,
  CAN_COUNTRY_ISO3CODE,
} from '../../util/constants';
import themeColors from '../../assets/styles/themeColors';
import { ReactComponent as House } from '../../assets/images/svg/house.svg';
import { ReactComponent as Building } from '../../assets/images/svg/building.svg';
import { ReactComponent as MapPinArea } from '../../assets/images/svg/map-pin-area.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/svg/check-icon.svg';
import { ReactComponent as CrossIcon } from '../../assets/images/svg/cross-icon.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-circle.svg';
import { variables } from '../../assets/styles/variables';
import {
  InputFieldWrapper,
  InputLabel,
  InputError,
} from '../InputFields/Components/InputComponents';
import { getStates } from '../../store/actions/common/commonActions';
import useGetCountries from '../../util/hooks/useGetCountries';
import LocationSearchField from './LocationSearchField';

const HiddenInput = styled.input`
  display: none;
  opacity: 0;
  cursor: 'context-menu';
  width: 0;
  height: 0;
`;

const DeliveryAddressTagsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const DeliveryAddressTagsLabel = styled.p`
  font-size: ${pxToRem(14)};
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  margin-right: 10px;
`;

const DeliveryAddressTagsList = styled.div`
  display: flex;
  gap: 10px;
`;

const DeliveryAddressTagElement = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${pxToRem(12)};
  font-weight: 400;
  color: ${themeColors.colorTextPrimary};
  background-color: ${themeColors.colorBackgroundSecondary};
  border: 1px solid transparent;
  border-radius: ${variables.borderRadius.borderRadius};
  padding: 4px 6px;
  cursor: pointer;
  ${(props) => props.selected && `border-color: ${themeColors.colorFocus}`};
`;

const DeliveryAddressFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(16)};
  flex: 1;
  overflow: auto;
`;

const DeliveryInformationBox = styled.div`
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px solid ${themeColors.colorPrimaryLight};
  background: ${themeColors.colorBackgroundSecondary};
  padding: ${pxToRem(12)};
`;

const DeliveryInformationLabel = styled.p`
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};
  margin-bottom: 6px;
`;

const DeliveryInformationItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;

  svg {
    margin-right: 6px;
  }
`;

const DeliveryInformationItemWrap = styled.div`
  display: flex;
  gap: 9px;
  margin-bottom: 4px;
`;

const TwoFieldsInRow = styled.div`
  display: flex;
  gap: 12px;
  > div {
    flex: 1;
  }
`;

const BankInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  color: #505575;

  svg {
    flex-shrink: 0;
  }
`;

const DeliveryAddressForm = ({
  stateOptions,
  setFieldValue,
  CountryIso3Code,
  StateRegion,
  onTagSelect,
  values,
  isAddingCCFlow,
  bankName,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { errors, touched, setFieldTouched } = useFormikContext();
  const countries = useGetCountries();

  const otherTagRef = useRef(null);

  useEffect(() => {
    if (values.TagType === 3 && otherTagRef.current) {
      otherTagRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [values.TagType]);

  useEffect(() => {
    dispatch(getStates(CountryIso3Code));
  }, [CountryIso3Code, dispatch]);

  const COUNTRY_OPTIONS = [
    ...countries.map((country) => ({
      value: country.Iso3Code,
      label: country.Caption,
    })),
  ];

  const handleChangeCountry = (event) => {
    if (event.target.value) {
      setFieldValue('CountryIso3Code', getCountryIso3(event.target.value));
    }
  };

  useEffect(() => {
    if (
      CountryIso3Code === USA_COUNTRY_ISO3CODE ||
      CountryIso3Code === CAN_COUNTRY_ISO3CODE
    ) {
      dispatch(getStates(CountryIso3Code));
    }
  }, [CountryIso3Code, dispatch]);

  const handlePlaceSelected = (place) => {
    const addressComponents = place.address_components;
    const getComponent = (type) =>
      addressComponents.find((c) => c.types.includes(type))?.long_name || '';
    const getShortComponent = (type) =>
      addressComponents.find((c) => c.types.includes(type))?.short_name || '';

    setFieldValue('location', place.formatted_address || '');

    setFieldValue(
      'AddressLine',
      `${getComponent('street_number')} ${getComponent('route')}`.trim(),
    );
    setFieldValue('City', getComponent('locality'));
    setFieldValue(
      'StateRegion',
      getShortComponent('administrative_area_level_1'),
    );
    setFieldValue('PostalCode', getComponent('postal_code'));
    if (isAddingCCFlow) {
      const countryIso2 = getShortComponent('country');
      const countryIso3 = getCountryIso3(countryIso2);
      setFieldValue('CountryIso3Code', countryIso3 || '');
    } else {
      // Default to USA for non-CC flow
      setFieldValue('CountryIso3Code', 'USA');
    }
  };

  const isStateRegionBasicInput =
    CountryIso3Code !== USA_COUNTRY_ISO3CODE &&
    CountryIso3Code !== CAN_COUNTRY_ISO3CODE;

  const handleChangeState = (event) => {
    if (event.target.value) {
      setFieldValue('StateRegion', event.target.value);
    }
  };

  const deliveryAddressTags = [
    { id: 1, tagType: t('buyWizard.homeLabel'), tagIcon: <House /> },
    {
      id: 2,
      tagType: t('buyWizard.officeLabel'),
      tagIcon: <Building />,
    },
    {
      id: 3,
      tagType: t('buyWizard.otherLabel'),
      tagIcon: <MapPinArea />,
    },
  ];

  const DeliveryAddressTag = ({ id, tagType, tagIcon, selected, onSelect }) => (
    <DeliveryAddressTagElement
      onMouseDown={() => onSelect(id)}
      selected={selected}
    >
      <div>{tagIcon}</div>
      <div>{tagType}</div>
    </DeliveryAddressTagElement>
  );

  DeliveryAddressTag.propTypes = {
    id: PropTypes.number,
    tagType: PropTypes.string,
    tagIcon: PropTypes.node,
    selected: PropTypes.bool,
    onSelect: PropTypes.func,
  };

  const handleSelectTag = (tagId) => {
    setFieldValue('TagType', tagId);
    setFieldTouched('TagType', true, false);

    const selectedTag = deliveryAddressTags.find((tag) => tag.id === tagId);
    const isOther = selectedTag?.tagType === t('buyWizard.otherLabel');

    if (!isOther) {
      setFieldValue('CustomTagName', '');
    }

    if (onTagSelect) onTagSelect(tagId);
  };

  const customStyles = {
    singleValue: (provided, state) => {
      const label = state.data?.label?.toLowerCase?.() || '';

      if (label === 'enter your state' || label === 'enter state') {
        return {
          ...provided,
          color: themeColors.colorTextSecondary,
        };
      }

      return {
        ...provided,
        color: themeColors.colorTextPrimary,
      };
    },
    placeholder: (provided) => ({
      ...provided,
      color: themeColors.colorTextSecondary,
    }),
  };

  const selectedState = stateOptions.find(
    (state) => state.value === StateRegion,
  );

  return (
    <DeliveryAddressFormWrapper>
      {bankName && (
        <DeliveryInformationBox>
          <BankInfoRow>
            <InfoIcon />
            <span>{t('buyWizard.bankStatementAddressInfo', { bankName })}</span>
          </BankInfoRow>
        </DeliveryInformationBox>
      )}

      {!bankName && !isAddingCCFlow && (
        <DeliveryInformationBox>
          <DeliveryInformationLabel>
            {t('buyWizard.deliveryInformation')}
          </DeliveryInformationLabel>
          <DeliveryInformationItemWrap>
            <DeliveryInformationItem>
              <CheckIcon />
              {t('buyWizard.usaDeliveryOnly')}
            </DeliveryInformationItem>
            <DeliveryInformationItem>
              <CrossIcon />
              {t('buyWizard.poBoxDeliveryNotAvailable')}
            </DeliveryInformationItem>
          </DeliveryInformationItemWrap>
        </DeliveryInformationBox>
      )}

      {!bankName && isAddingCCFlow && (
        <InputFieldWrapper>
          <HiddenInput
            type="text"
            autoComplete="country"
            value={CountryIso3Code}
            onChange={handleChangeCountry}
          />

          <SelectField
            name="CountryIso3Code"
            label={t('creditCard.fields.country')}
            disabled={!countries.length}
            options={COUNTRY_OPTIONS}
            value={{
              value: CountryIso3Code,
              label: countries.find(
                (country) => country.Iso3Code === CountryIso3Code,
              )?.Caption,
            }}
            selectOption={({ value }) => {
              setFieldValue('CountryIso3Code', value);
            }}
          />
        </InputFieldWrapper>
      )}

      <LocationSearchField
        onPlaceSelected={handlePlaceSelected}
        restrictToUs={!isAddingCCFlow}
      />

      <InputFieldWrapper>
        <Field
          component={TextField}
          name="AddressLine"
          autocomplete="street-address"
          placeholder={t('buyWizard.enterStreet')}
          label={t('buyWizard.streetAddress')}
        />
      </InputFieldWrapper>
      <InputFieldWrapper>
        <Field
          component={TextField}
          name="AddressLine2"
          autocomplete="address-line2"
          placeholder={t('buyWizard.enterAddressLine2')}
          label={t('buyWizard.addressLine2')}
        />
      </InputFieldWrapper>
      <TwoFieldsInRow>
        <InputFieldWrapper>
          <Field
            component={TextField}
            name="City"
            label={t('buyWizard.city')}
            placeholder={t('buyWizard.enterCity')}
          />
        </InputFieldWrapper>
        {!isStateRegionBasicInput ? (
          <InputFieldWrapper>
            <HiddenInput
              type="text"
              autoComplete="address-level1"
              value={StateRegion}
              onChange={handleChangeState}
            />
            <SelectField
              name="StateRegion"
              label={t('buyWizard.state')}
              placeholder={t('buyWizard.enterState')}
              value={selectedState || null}
              options={stateOptions}
              selectOption={({ value }) => setFieldValue('StateRegion', value)}
              customStyles={customStyles}
            />
          </InputFieldWrapper>
        ) : (
          <InputFieldWrapper>
            <Field
              component={TextField}
              name="StateRegion"
              label={t('buyWizard.state')}
              placeholder={t('buyWizard.enterState')}
              autoComplete="address-level1"
            />
          </InputFieldWrapper>
        )}
      </TwoFieldsInRow>
      <InputFieldWrapper>
        <Field
          component={TextField}
          name="PostalCode"
          label={t('buyWizard.zipCode')}
          placeholder={t('buyWizard.enterZipCode')}
          autoComplete="postal-code"
        />
      </InputFieldWrapper>
      {!bankName && (
        <InputFieldWrapper>
          <InputLabel>{t('buyWizard.selectAddressName')}</InputLabel>
          <DeliveryAddressTagsWrapper>
            <DeliveryAddressTagsLabel>
              {t('buyWizard.saveAs')}
            </DeliveryAddressTagsLabel>
            <DeliveryAddressTagsList>
              {deliveryAddressTags.map((tag) => (
                <DeliveryAddressTag
                  key={tag.id}
                  id={tag.id}
                  tagIcon={tag.tagIcon}
                  tagType={tag.tagType}
                  selected={values.TagType === tag.id}
                  onSelect={handleSelectTag}
                />
              ))}
            </DeliveryAddressTagsList>
          </DeliveryAddressTagsWrapper>
          {errors.TagType && touched.TagType && (
            <InputError>{errors.TagType}</InputError>
          )}
        </InputFieldWrapper>
      )}
      {!bankName && values.TagType === 3 && (
        <InputFieldWrapper ref={otherTagRef}>
          <Field
            component={TextField}
            name="CustomTagName"
            placeholder={t('buyWizard.enterTagName')}
            autoComplete="off"
          />
        </InputFieldWrapper>
      )}
    </DeliveryAddressFormWrapper>
  );
};

export default DeliveryAddressForm;

DeliveryAddressForm.propTypes = {
  stateOptions: PropTypes.arrayOf({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  CountryIso3Code: PropTypes.string,
  StateRegion: PropTypes.string,
  setFieldValue: PropTypes.func,
  onTagSelect: PropTypes.func,
  values: PropTypes.shape({
    TagType: PropTypes.number,
  }),
  isAddingCCFlow: PropTypes.bool,
  bankName: PropTypes.string,
};
